//*Libraries imports
import React, { useRef, useState } from "react";
import { InfoIcon } from "lucide-react";
import { cn } from "@/lib/utils";

//* Features imports
import ApplyTemplateTags from "@/features/template/ApplyTags";
import ApplyTechnicalTags from "@/features/techinicaldocument/ApplyTags";
import { TemplateOutputDTO } from "@/features/template/types";
import { TechinicalOutputDTO } from "@/features/techinicaldocument/types";
import { ClientOutputDTO } from "@/features/Client/types";
import { IActivity } from "@/features/Element/types";
import { DocumentOutputDTO } from "@/features/document/types";

//* Components imports
import { TemplateCard } from "./TemplateCard";
import { TechnicalCard } from "./TechnicalCard";
import { DocumentCard } from "./DocumentCard";

type ActivityDetailsProps = {
  activity: Pick<
    IActivity,
    "_id" | "name" | "documents" | "techinicals" | "templates"
  > & { index: number };
  documents: DocumentOutputDTO[];
  techinicals?: TechinicalOutputDTO[];
  templates?: TemplateOutputDTO[];
  docsFilled: Record<string, any>;
  loadingUploads: Record<string, boolean>;
  onUploadFile: (docId: string, file: File) => void;
  onDownload: (path: string) => void;
  hasBindingQuestion?: any;
  client?: ClientOutputDTO;
};

export function ActivityDetails(props: ActivityDetailsProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const selectedDocRef = useRef<string | null>(null);

  const [selectedTemplate, setSelectedTemplate] =
    useState<TemplateOutputDTO | null>(null);
  const [selectedTechnical, setSelectedTechnical] =
    useState<TechinicalOutputDTO | null>(null);

  console.log("has binding", props.hasBindingQuestion);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0] && selectedDocRef.current) {
      props.onUploadFile(selectedDocRef.current, e.target.files[0]);
    }
    e.target.value = "";
  };

  const handleUploadClick = (docId: string) => {
    selectedDocRef.current = docId;
    fileInputRef.current?.click();
  };

  if (!props.activity) return null;

  return (
    <div className="bg-white rounded-2xl shadow-sm p-6 h-full overflow-y-auto flex flex-col">
      <div className="relative flex items-center justify-center mb-8 pb-4 border-b border-gray-100">
        <div className="flex items-center gap-3">
          <span className="text-gray-500 font-medium text-lg">
            {String(props.activity.index).padStart(2, "0")}
          </span>
          <span className="text-gray-300">|</span>
          <span className="font-semibold text-lg text-gray-700">
            {props.activity.name}
          </span>
        </div>
        {/* <button
          title="Informações"
          className="absolute right-0 text-gray-400 hover:text-gray-600"
        >
          <InfoIcon className="size-4" />
        </button> */}
      </div>

      <div className="flex-1">
        {/* Documentos Section */}
        {props.activity.documents && props.activity.documents.length > 0 && (
          <div className="mb-8">
            <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-4">
              Documentos Necessários
            </h3>
            {/* Document List Style Imagem */}
            <div className="mb-6">
              <ul className="space-y-2">
                {props.activity.documents?.map((docId: string, i: number) => {
                  const doc = props.documents.find((d) => d._id === docId);
                  const isFilled = !!props.docsFilled[docId];
                  return (
                    <li
                      key={docId}
                      className={cn(
                        "text-base font-medium",
                        isFilled ? "text-blue-500" : "text-gray-600",
                      )}
                    >
                      {String(i + 1).padStart(2, "0")} -{" "}
                      {doc?.name || "Documento"}
                    </li>
                  );
                })}
              </ul>
            </div>

            <div className="mb-6">
              {props.hasBindingQuestion && (
                <a
                  target="_blank"
                  href={`${window.location.origin}/forms/${props.hasBindingQuestion.phaseFormId}/${props.hasBindingQuestion.processId}/no-product/${props.client._id}`}
                >
                  Link para preencher os documentos
                </a>
              )}
            </div>
            {/* Document Grid Style Imagem */}
            <div className="grid grid-cols-4 gap-4">
              {props.activity.documents?.map((docId: string, i: number) => {
                const doc = props.documents.find((d) => d._id === docId);
                const filledDoc = props.docsFilled[docId];

                return (
                  <DocumentCard
                    key={docId}
                    doc={doc}
                    index={i}
                    filledDoc={filledDoc}
                    isLoading={props.loadingUploads[docId]}
                    hasBindingQuestion={props.hasBindingQuestion}
                    onDownload={props.onDownload}
                    onUpload={handleUploadClick}
                  />
                );
              })}
            </div>
          </div>
        )}

        {(!props.activity.documents ||
          props.activity.documents.length === 0) && (
          <div className="mb-8">
            <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-4">
              Documentos Necessários
            </h3>
            <p className="text-gray-400 text-sm italic">
              Nenhum documento necessário.
            </p>
          </div>
        )}

        {/* Peças Técnicas Section */}
        {props.activity.techinicals &&
          props.activity.techinicals.length > 0 && (
            <div className="mb-8 pt-8 border-t border-gray-100">
              <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-4">
                Peças Técnicas
              </h3>
              {/* List Style */}
              <div className="mb-6">
                <ul className="space-y-2">
                  {props.activity.techinicals.map(
                    (techId: string | TechinicalOutputDTO, i: number) => {
                      const tech =
                        props.techinicals?.find(
                          (t: TechinicalOutputDTO) =>
                            t._id ===
                            (typeof techId === "string" ? techId : techId._id),
                        ) || (typeof techId === "object" ? techId : null);

                      if (!tech) return null;

                      return (
                        <li
                          key={tech._id}
                          className="text-base font-medium text-blue-500"
                        >
                          {String(i + 1).padStart(2, "0")} -{" "}
                          {tech.name || tech.document_name || "Peça Técnica"}
                        </li>
                      );
                    },
                  )}
                </ul>
              </div>

              <div className="grid grid-cols-4 gap-4">
                {props.activity.techinicals.map(
                  (techId: string | TechinicalOutputDTO, i: number) => {
                    const tech =
                      props.techinicals?.find(
                        (t: TechinicalOutputDTO) =>
                          t._id ===
                          (typeof techId === "string" ? techId : techId._id),
                      ) || (typeof techId === "object" ? techId : null);

                    if (!tech) return null;

                    return (
                      <TechnicalCard
                        key={tech._id}
                        technical={tech}
                        index={i}
                        clientId={props.client?._id}
                        onSelect={setSelectedTechnical}
                      />
                    );
                  },
                )}
              </div>
            </div>
          )}

        {/* Templates Section */}
        {props.activity.templates && props.activity.templates.length > 0 && (
          <div className="mb-8 pt-8 border-t border-gray-100">
            <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-4">
              Templates
            </h3>
            {/* List Style */}
            <div className="mb-6">
              <ul className="space-y-2">
                {props.activity.templates.map(
                  (tempId: string | TemplateOutputDTO, i: number) => {
                    const temp =
                      props.templates?.find(
                        (t: TemplateOutputDTO) =>
                          t._id ===
                          (typeof tempId === "string" ? tempId : tempId._id),
                      ) || (typeof tempId === "object" ? tempId : null);

                    if (!temp) return null;

                    return (
                      <li
                        key={temp._id}
                        className="text-base font-medium text-blue-500"
                      >
                        {String(i + 1).padStart(2, "0")} -{" "}
                        {temp.name || temp.document_name || "Template"}
                      </li>
                    );
                  },
                )}
              </ul>
            </div>

            <div className="grid grid-cols-4 gap-4">
              {props.activity.templates.map(
                (tempId: string | TemplateOutputDTO, i: number) => {
                  const temp =
                    props.templates?.find(
                      (t: TemplateOutputDTO) =>
                        t._id ===
                        (typeof tempId === "string" ? tempId : tempId._id),
                    ) || (typeof tempId === "object" ? tempId : null);

                  if (!temp) return null;

                  return (
                    <TemplateCard
                      key={temp._id}
                      template={temp}
                      index={i}
                      clientId={props.client?._id}
                      onSelect={setSelectedTemplate}
                    />
                  );
                },
              )}
            </div>
          </div>
        )}
      </div>

      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        className="hidden"
      />

      {selectedTemplate && (
        <ApplyTemplateTags
          isOpen={!!selectedTemplate}
          onClose={() => setSelectedTemplate(null)}
          template={selectedTemplate}
          clientId={props.client?._id}
        />
      )}

      {selectedTechnical && (
        <ApplyTechnicalTags
          isOpen={!!selectedTechnical}
          onClose={() => setSelectedTechnical(null)}
          tecnical={selectedTechnical}
          clientId={props.client?._id}
        />
      )}
    </div>
  );
}
